import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { supportAPI } from '../../features/auth/services/api';
import './MentorSupportModal.css';

const MentorSupportModal = ({ onClose }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [status, setStatus]   = useState('idle');   // idle | sending | success | error
  const [error, setError]     = useState('');

  const canSubmit = subject.trim().length >= 3 && message.trim().length >= 10;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be under 5 MB');
        e.target.value = '';
        return;
      }
      setAttachment(file);
      setError('');
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    // Reset file input
    const fileInput = document.getElementById('ms-attachment');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus('sending');
    setError('');
    try {
      await supportAPI.contact({
        subject: subject.trim(),
        message: message.trim(),
        attachment: attachment || undefined,
      });
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    }
  };

  return ReactDOM.createPortal(
    <div className="ms-overlay" onClick={onClose}>
      <div className="ms-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ms-close" onClick={onClose}>✕</button>

        {status === 'success' ? (
          <div className="ms-success">
            <div className="ms-success-icon">✅</div>
            <h3>Message Sent!</h3>
            <p>Our mentor team will get back to you soon via email.</p>
            <button className="ms-btn ms-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="ms-header">
              <div className="ms-header-icon">🎓</div>
              <div>
                <h2 className="ms-title">Mentor Support</h2>
                <p className="ms-subtitle">Have a question or need help? Our mentors are here for you.</p>
              </div>
            </div>

            <form className="ms-form" onSubmit={handleSubmit}>
              <div className="ms-field">
                <label className="ms-label" htmlFor="ms-subject">Subject</label>
                <input
                  id="ms-subject"
                  type="text"
                  className="ms-input"
                  placeholder="e.g. Help with database setup"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  autoFocus
                />
              </div>

              <div className="ms-field">
                <label className="ms-label" htmlFor="ms-message">Description</label>
                <textarea
                  id="ms-message"
                  className="ms-textarea"
                  placeholder="Describe your question or issue in detail…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={5000}
                  rows={6}
                />
                <span className="ms-char-count">{message.length} / 5000</span>
              </div>

              <div className="ms-field">
                <label className="ms-label" htmlFor="ms-attachment">
                  Attachment <span className="ms-optional">(optional, max 5 MB)</span>
                </label>
                <div className="ms-file-wrapper">
                  <input
                    id="ms-attachment"
                    type="file"
                    className="ms-file-input"
                    onChange={handleFileChange}
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.zip,.doc,.docx,.txt,.csv,.xlsx"
                  />
                  {!attachment && (
                    <label htmlFor="ms-attachment" className="ms-file-label">
                      <span className="ms-file-icon">📎</span>
                      <span>Choose a file</span>
                    </label>
                  )}
                  {attachment && (
                    <div className="ms-file-selected">
                      <span className="ms-file-icon">📄</span>
                      <span className="ms-file-name">{attachment.name}</span>
                      <span className="ms-file-size">({(attachment.size / 1024).toFixed(0)} KB)</span>
                      <button type="button" className="ms-file-remove" onClick={removeAttachment}>✕</button>
                    </div>
                  )}
                </div>
              </div>

              {error && <div className="ms-error">{error}</div>}

              <div className="ms-actions">
                <button type="button" className="ms-btn ms-btn-ghost" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ms-btn ms-btn-primary"
                  disabled={!canSubmit || status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : '📩 Send Message'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MentorSupportModal;
